import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { TargetAudience } from '@/lib/gcc-content'
import { Users, Briefcase, Rocket, GraduationCap, LucideIcon } from 'lucide-react'
import { FadeIn, SlideUp } from '@/components/animations'
import { motion } from 'framer-motion'
import { useReducedMotion } from '@/hooks/useReducedMotion'

const iconMap: Record<string, LucideIcon> = {
  Users,
  Briefcase,
  Rocket,
  GraduationCap
}

interface TargetAudienceSectionProps {
  audiences: TargetAudience[]
  sectionTitle?: string
  className?: string
}

interface AudienceCardProps {
  audience: TargetAudience
}

const AudienceCard = ({ audience }: AudienceCardProps) => {
  const prefersReducedMotion = useReducedMotion()
  // Safely get icon from map, fallback to Users if not found or invalid
  const IconComponent = (audience.icon && iconMap[audience.icon]) || Users

  const iconVariants = {
    initial: { scale: 1 },
    hover: {
      scale: prefersReducedMotion ? 1 : 1.05,
      transition: { duration: 0.2, ease: "easeOut" }
    }
  }

  return (
    <Card
      data-testid="audience-card"
      className="group h-full bg-white/50 backdrop-blur-sm border-0 shadow-sm hover:shadow-xl hover:bg-white hover:-translate-y-0.5 transition-all duration-300"
    >
      <CardHeader className="text-center pb-6 pt-8">
        <motion.div
          className="mx-auto w-14 h-14 rounded-xl bg-slate-50 group-hover:bg-slate-100 flex items-center justify-center mb-4 transition-all duration-300"
          variants={iconVariants}
          initial="initial"
          whileHover="hover"
        >
          <IconComponent className="w-7 h-7 text-slate-600" />
        </motion.div>
        <h3 className="text-lg font-semibold text-slate-900 leading-tight">
          {audience.title}
        </h3>
      </CardHeader>

      <CardContent className="pt-0 pb-8 px-6">
        <p className="text-slate-600 leading-relaxed text-center">
          {audience.description}
        </p>
      </CardContent>
    </Card>
  )
}

const TargetAudienceSection = ({
  audiences,
  sectionTitle = "Who Is It For?",
  className = ''
}: TargetAudienceSectionProps) => {
  return (
    <section
      data-testid="target-audiences"
      className={`py-24 px-4 sm:px-6 lg:px-8 bg-slate-50/30 ${className}`}
    >
      <div className="max-w-6xl mx-auto">
        {/* Section Title */}
        <FadeIn className="text-center mb-20">
          <h2 className="text-3xl font-light text-slate-900 lg:text-4xl tracking-tight">
            {sectionTitle}
          </h2>
        </FadeIn>

        {/* Audiences Grid */}
        <div
          data-testid="audiences-grid"
          className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4 xl:gap-12"
        >
          {audiences.map((audience, index) => (
            <SlideUp
              key={audience.id}
              delay={index * 0.1}
              duration={0.5}
              distance={30}
            >
              <AudienceCard audience={audience} />
            </SlideUp>
          ))}
        </div>
      </div>
    </section>
  )
}

export default TargetAudienceSection